const BUS_KEY = 'bs:tab-actions';
const sessionKey = '__bs_session_id__';
export const MERGE_SYNCED_STATE = '__MERGE_SYNCED_STATE__';

let currentNamespace = null;
let previousNamespace = null;

export const setCrossTabNamespace = (ns) => {
  const newNs = ns || null;
  
  if (previousNamespace !== null && newNs !== previousNamespace && newNs !== null) {
    try {
      localStorage.removeItem(BUS_KEY);
      if (previousNamespace && externalWindowsCount[previousNamespace]) {
        delete externalWindowsCount[previousNamespace];
      }
    } catch {}
  }
  
  previousNamespace = currentNamespace;
  currentNamespace = newNs;
};

export const getCurrentNamespace = () => currentNamespace;

const externalWindowsCount = {};

export const registerExternalWindow = (namespace) => {
  if (!namespace) return;
  externalWindowsCount[namespace] = (externalWindowsCount[namespace] || 0) + 1;
};

export const unregisterExternalWindow = (namespace) => {
  if (!namespace) return;
  if (externalWindowsCount[namespace] > 0) {
    externalWindowsCount[namespace]--;
    if (externalWindowsCount[namespace] === 0) {
      delete externalWindowsCount[namespace];
    }
  }
};

const hasExternalWindows = (namespace) => {
  if (!namespace) return false;
  return (externalWindowsCount[namespace] || 0) > 0;
};

export { hasExternalWindows };

export const getSessionId = () => {
  let id = null;
  try {
    id = sessionStorage.getItem(sessionKey);
    if (!id) {
      id = Math.random().toString(36).slice(2);
      sessionStorage.setItem(sessionKey, id);
    }
  } catch {
    id = Math.random().toString(36).slice(2);
  }
  return id;
};

export const ACTIONS_WHITELIST = new Set([
  'UPDATE_TABLE',
  'GET_COLLECTION_TABLE',
  'CLOSE_TAB',
  'SET_ACTIVE_TAB',
  'UNASSIGN_FROM_COLLECTIONS',
  'ADD_TO_NEW_COLLECTION',
  'COPY_TO_COLLECTION',
  'COPY_TO_COLLECTION_UNSAVED',
  'ASSIGN_TO_COLLECTION',
  'REPLACE_TAB',
  'SET_TABS_TABLE_STATE',
  'SET_CURRENT_TABLE_CELL',
  'RESET_TABLE_AND_TABS',
  'LOAD_TAB_SETTINGS',
  'SET_COLUMN_SCHEME',
  'DELETE_COLUMN',
  'CLEAR_TEMPORARY_SETTINGS',
  'CREATE_SETTING_COLOR_SCHEME',
  'SET_COLOR_PRIMARY_COLUMN',
  'DELETE_SCHEME_SETTING',
  'RESET_SCHEME_SETTINGS',
  'UPDATE_FILTER',
  'UPDATE_TEMPLATE_SETTINGS',
  'CREATE_SETTING_FILTER_SCHEME',
  'LOAD_CUSTOM_PROPERTIES',
  'SET_CUSTOM_PROPERTIES',
  'EDIT_CUSTOM_PROPERTIES',
  'DELETE_CUSTOM_PROPERTIES',
]);

const KINDS = {
  ACTION: 'redux',
  REQUEST_STATE: 'request_state',
  PUSH_STATE: 'push_state',
};

const sessionId = getSessionId();

const post = (msg) => {
  try {
    if (!currentNamespace || !hasExternalWindows(currentNamespace)) {
      return;
    }
    localStorage.setItem(
      BUS_KEY,
      JSON.stringify({ ...msg, ns: currentNamespace || null })
    );
  } catch {}
};

const sanitizeActionForWire = (action) => {
  const { meta, ...rest } = action || {};
  return { ...rest, meta: undefined };
};

const pickSyncState = (full) => {
  if (!full) return {};
  const { table, tabsSettings, translations, colorSidebar, condition, viewer } = full;
  const sanitizedTable = table ? {
    ...table,
    currentTableState: table.currentTableState ? {
      ...table.currentTableState,
      selectedRows: [],
    } : table.currentTableState,
    tabs: table.tabs ? table.tabs.map((tab) => ({
      ...tab,
      selectedRows: [],
      selectedItems: [],
    })) : table.tabs,
  } : table;
  return {
    table: sanitizedTable,
    tabsSettings,
    translations,
    colorSidebar,
    condition,
    viewer: {
      customProperties: viewer?.customProperties ?? [],
      customPropertiesValues: viewer?.customPropertiesValues ?? [],
      templateSettings: viewer?.templateSettings ? { ...viewer.templateSettings } : {},
    },
  };
};

let mountedStore = null;

export const createCrossTabMiddleware = () => {
  return () => (next) => (action) => {
    if (action?.meta?.source === 'remote') {
      return next(action);
    }
    
    const result = next(action);
    try {
      if (ACTIONS_WHITELIST.has(action.type)) {
        if (currentNamespace && hasExternalWindows(currentNamespace)) {
          post({
            v: 1,
            ts: Date.now(),
            from: sessionId,
            kind: KINDS.ACTION,
            action: sanitizeActionForWire(action),
          });
        }
      }
    } catch {}
    return result;
  };
};

export const mountCrossTabListener = (store) => {
  mountedStore = store;
  const { dispatch, getState } = store;
  let initialMergeDone = false;
  let storageListener = null;

  const onStorage = (e) => {
    if (e.storageArea !== localStorage || e.key !== BUS_KEY) return;
    
  const localNs = currentNamespace || null;
  if (!localNs || !hasExternalWindows(localNs)) {
    return;
  }
    
    try {
      const msg = e.newValue ? JSON.parse(e.newValue) : null;
      if (!msg || msg.from === sessionId) return;

      const msgNs = msg.ns || null;
      
      if (msgNs === null || msgNs !== localNs) {
        return;
      }
      
      if (msg.kind === KINDS.ACTION) {
        const { type, payload, ...rest } = msg.action || {};
        if (!ACTIONS_WHITELIST.has(type)) return;
        dispatch({ type, payload, ...rest, meta: { source: 'remote' } });
        return;
      }
      if (msg.kind === KINDS.REQUEST_STATE) {
        if (hasExternalWindows(localNs)) {
          post({
            v: 1,
            ts: Date.now(),
            from: sessionId,
            kind: KINDS.PUSH_STATE,
            state: pickSyncState(getState()),
            reason: 'init',
          });
        }
        return;
      }
      if (msg.kind === KINDS.PUSH_STATE) {
        const reason = msg.reason || 'init';
        if (!initialMergeDone || reason === 'finalize') {
          initialMergeDone = true;
          dispatch({
            type: MERGE_SYNCED_STATE,
            payload: msg.state || {},
            meta: { source: 'remote', reason },
          });
        }
        return;
      }
    } catch {}
  };

  const checkAndSetupListener = () => {
    const localNs = currentNamespace || null;
    if (localNs && hasExternalWindows(localNs)) {
      if (!storageListener) {
        storageListener = onStorage;
        window.addEventListener('storage', storageListener);
      }
    } else {
      if (storageListener) {
        window.removeEventListener('storage', storageListener);
        storageListener = null;
      }
    }
  };

  checkAndSetupListener();

  const namespaceCheckInterval = setInterval(() => {
    checkAndSetupListener();
  }, 1000);

  setTimeout(() => {
    if (currentNamespace && hasExternalWindows(currentNamespace)) {
      post({ v: 1, ts: Date.now(), from: sessionId, kind: KINDS.REQUEST_STATE });
    }
  }, 0);

  return () => {
    clearInterval(namespaceCheckInterval);
    if (storageListener) {
      window.removeEventListener('storage', storageListener);
    }
  };
};

export const pushCurrentState = (reason = 'manual') => {
  if (!mountedStore) return;
  try {
    const state = mountedStore.getState();
    post({
      v: 1,
      ts: Date.now(),
      from: sessionId,
      kind: KINDS.PUSH_STATE,
      state: pickSyncState(state),
      reason,
    });
  } catch {}
};